import SchemaOrg from '@/components/base/SchemaOrg';
import GuideLayout from '@/pages/guide/components/GuideLayout';

const safetySchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Is Superbuy Sheet Safe? Complete Safety & Trust Guide 2026',
  description: 'Learn about Superbuy Sheet safety, privacy protection, secure browsing, and transparent shopping practices. Your trusted guide to safe online product discovery.',
  image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20a%20modern%20secure%20digital%20shopping%20concept%20with%20soft%20warm%20lighting%2C%20cream%20and%20sage%20green%20color%20palette%2C%20laptop%20with%20shopping%20interface%20on%20wooden%20desk%2C%20subtle%20lock%20icon%20motif%2C%20trustworthy%20and%20calm%20atmosphere%2C%20editorial%20productivity%20aesthetic%2C%20high%20detail%20texture&width=1600&height=700&seq=guide-safety-hdr-sec&orientation=landscape',
  author: { '@type': 'Person', name: 'Alex Carter', jobTitle: 'Shopping Research Analyst' },
  datePublished: '2026-07-01',
  dateModified: '2026-07-13',
  publisher: { '@type': 'Organization', name: 'Superbuy Sheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png' } },
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://superbuysheet.click/guide/safety' },
};

export default function SafetyGuide() {
  return (
    <>
      <SchemaOrg data={safetySchema} />
      <GuideLayout
        title="Is Superbuy Sheet Safe? Complete Safety & Trust Guide 2026"
        description="Learn about Superbuy Sheet safety, privacy protection, secure browsing, and transparent shopping practices. Your trusted guide to safe online product discovery."
        breadcrumbLabel="Safety Guide"
        readTime="7 min read"
        imageUrl="https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20a%20modern%20secure%20digital%20shopping%20concept%20with%20soft%20warm%20lighting%2C%20cream%20and%20sage%20green%20color%20palette%2C%20laptop%20with%20shopping%20interface%20on%20wooden%20desk%2C%20subtle%20lock%20icon%20motif%2C%20trustworthy%20and%20calm%20atmosphere%2C%20editorial%20productivity%20aesthetic%2C%20high%20detail%20texture&width=1600&height=700&seq=guide-safety-hdr-sec&orientation=landscape"
        imageAlt="Superbuy Sheet safety and trust guide"
        internalLinks={[
          { label: 'Complete Guide', to: '/guide', icon: 'ri-book-open-line' },
          { label: 'Beginner Guide', to: '/guide/beginner', icon: 'ri-compass-line' },
          { label: 'FAQ', to: '/guide/faq', icon: 'ri-question-answer-line' },
          { label: 'Main Website', href: 'https://kaspreadsheet.com', icon: 'ri-external-link-line' },
        ]}
      >
        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Understanding Superbuy Sheet Safety
          </h2>
          <p>
            Safety is one of the most common questions new users ask about the
            <strong className="text-foreground-950"> Superbuy Sheet</strong> platform, and it is a
            question we take seriously. The short answer is yes — Superbuy Sheet is designed with
            safety and transparency as foundational principles. As an informational and navigational
            resource, the platform does not process payments, store sensitive personal data, or require
            user registration for basic browsing. This architecture inherently limits exposure to the
            types of security risks commonly associated with e-commerce platforms.
          </p>
          <p>
            All product links on <strong className="text-foreground-950">Superbuy Sheet</strong> direct
            users to our main website at kaspreadsheet.com, which maintains industry-standard security
            protocols including encrypted connections and secure transaction processing. The separation
            between discovery (Superbuy Sheet) and transaction (kaspreadsheet.com) creates an additional
            layer of protection, ensuring that your browsing activity remains private while your
            purchasing decisions benefit from the security infrastructure of a dedicated e-commerce platform.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            How We Protect Your Privacy
          </h2>
          <div className="space-y-4">
            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-eye-off-line text-sm"></i>
                </span>
                No Personal Data Collection
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                We do not require account creation, email registration, or any form of personal
                identification to browse categories and access product information. Your shopping
                discovery journey remains completely anonymous unless you choose to engage with
                the main platform.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-lock-line text-sm"></i>
                </span>
                Secure External Links
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Every outbound link to kaspreadsheet.com and other external resources uses standard
                security attributes to prevent potential security vulnerabilities associated with
                cross-origin navigation.
              </p>
            </div>

            <div className="p-5 bg-background-100/70 rounded-xl border border-background-200/50">
              <h4 className="font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-file-text-line text-sm"></i>
                </span>
                Transparent Operations
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                We clearly disclose our role as an independent informational platform. We are not
                affiliated with Superbuy officially, and we maintain full transparency about how
                our website operates, what data (if any) is collected, and how links function.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Safety Tips for Online Shoppers
          </h2>
          <div className="space-y-3">
            {[
              'Always verify that you are on the correct website (superbuysheet.click for discovery, kaspreadsheet.com for purchases) before entering any personal information.',
              'Use strong, unique passwords for any shopping accounts and enable two-factor authentication when available.',
              'Review product descriptions and seller information thoroughly before making purchasing decisions.',
              'Keep your browser and security software updated to protect against the latest online threats.',
              'Trust your instincts — if a deal seems too good to be true or a listing appears suspicious, take extra time to verify before proceeding.',
            ].map((tip, i) => (
              <div key={i} className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 mt-0.5 text-xs font-semibold">
                  {i + 1}
                </span>
                <p className="text-sm text-foreground-600 leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading text-2xl font-semibold text-foreground-950 mb-4">
            Our Commitment to a Safe Shopping Experience
          </h2>
          <p>
            The <strong className="text-foreground-950">Superbuy Sheet</strong> team is committed to
            maintaining a safe, transparent, and trustworthy platform for all users. We regularly review
            our security practices, update our content to reflect the most current information, and
            welcome feedback from the community about how we can continue to improve. If you ever have
            questions or concerns about safety while using Superbuy Sheet, we encourage you to reach
            out through the main platform at kaspreadsheet.com.
          </p>
        </section>
      </GuideLayout>
    </>
  );
}